<template>
  <v-overlay :dark="false" :absolute="false" :value="show">
    <v-sheet color="rgb(33,33,33,0)" width="600">
      <v-card v-if="!creationResult" class="d-flex flex-column" width="600">
        <v-card-title>
          <div>{{ creating ? "Building your application" : "Create New Application" }}</div>
          <v-spacer></v-spacer>
          <v-icon id="closeCreateApplicationOverlay" @click="close()"
            >mdi-close</v-icon
          >
        </v-card-title>
        <v-fade-transition mode="out-in">
          <div v-if="!creating" key="application-create-form">
            <v-card-text>
              You want to use an Schaeffler API from within your application? Great!
              🥳<br />
              Please register your application!
            </v-card-text>
            <v-card-text class="">
          <v-form
            ref="formCreate"
            v-model="createValid"
            v-on:submit.prevent="nop"
          >
            <v-row no-gutters>
              <v-col cols="9">
                <v-text-field
                  v-model="appNameCreate"
                  id="inputApplicationName"
                  label="Application Name"
                  placeholder="give your application a nice name"
                  outlined
                  :rules="[notEmpty, min, rules.validName]"
                  :error-messages="serverErrorCreate"
                  dense
                  maxlength="50"
                  counter
                  autocomplete="new-password"
                  color="#006c27"
                >
                </v-text-field>
              </v-col>
              <v-col>
                <v-btn
                  class="ml-6 white--text"
                  id="createButton"
                  color="#006c27"
                  style="display: inline-block"
                  :disabled="!createValid || creating"
                  @click="createApplication()"
                >
                  Create
                </v-btn>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="9" id="selectedEnvironment">
                <v-select
                  v-model="selectedEnvironment"
                  :items="environments"
                  label="Environment"
                  outlined
                  class="mb-3"
                  :menu-props="{ bottom: true, offsetY: true }"
                  color="#006c27"
                  hide-details
                  :rules="[notEmpty]"
                  dense
                ></v-select>
              </v-col>
              <v-col>
                <v-btn
                  id="environmentHelpMeButtonCreateApplication"
                  @click="showEnvironmentHelp = true"
                  class="ml-6"
                  color="#006c27"
                  text
                  style="
                        display: inline-block;
                        text-transform: none;
                        letter-spacing: 0;
                        text-align: left;
                      "
                >
                  Help me!
                </v-btn>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="9" id="selectedPermissionModel" class="mb-4">
                <v-select
                  v-model="selectedPermissionModel"
                  :items="permissionModels"
                  label="Permission Model"
                  outlined
                  :menu-props="{ bottom: true, offsetY: true }"
                  color="#006c27"
                  :rules="[notEmpty]"
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col>
                <v-btn
                  id="permissionHelpMeButtonCreateApplication"
                  @click="showPermissionModelHelp = true"
                  class="ml-6"
                  color="#006c27"
                  text
                  style="
                        display: inline-block;
                        text-transform: none;
                        letter-spacing: 0;
                        text-align: left;
                      "
                >
                  Help me!
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
            </v-card-text>
          </div>
          <section v-else key="application-create-progress" class="build-progress" aria-live="polite">
            <application-build-animation :progress-step="demoStep" />
            <div class="build-kicker">APPLICATION ASSEMBLY</div>
            <h2>{{ creationProgress }}</h2>
            <p>Putting the pieces together. Please wait and enjoy!</p>
          </section>
        </v-fade-transition>
      </v-card>
    </v-sheet>
    <environment-dialog v-model="showEnvironmentHelp"></environment-dialog>
    <permission-dialog v-model="showPermissionModelHelp"></permission-dialog>
    <application-creation-result-dialog
      :value="creationResult"
      @close="close"
      @open-application="openApplication"
    ></application-creation-result-dialog>
  </v-overlay>
</template>

<script>
import EnvironmentDialog from "./EnvironmentDialog.vue";
import PermissionDialog from "./PermissionDialog.vue";
import ApplicationBuildAnimation from "./ApplicationBuildAnimation.vue";
import ApplicationCreationResultDialog from "./ApplicationCreationResultDialog.vue";
import cass from "../api/cass";
import {
  applicationCreationProgressLabel,
  applicationCreationResultFromError,
  applicationCreationResultFromOperation,
  waitForApplicationCreationOperation,
} from "../utils/applicationCreationOperation";

const progressStepByState = {
  queued: 0,
  creating_api_application: 0,
  creating_identity: 1,
  configuring_identity: 2,
  finalizing: 3,
  complete: 3,
  failed: 3,
};

export default {
  name: "ApplicationCreateOverlay",
  components: { ApplicationBuildAnimation, ApplicationCreationResultDialog, EnvironmentDialog, PermissionDialog },
  props: {
    value: Boolean,
  },
  data: () => ({
    showEnvironmentHelp: false,
    showPermissionModelHelp: false,
    appNameCreate: "",
    selectedEnvironment: "",
    environments: null,
    selectedPermissionModel: "",
    permissionModels: ["DELEGATED", "APPLICATION"],
    createValid: false,
    serverErrorCreate: "",
    creating: false,
    creationProgress: "Creating application",
    demoStep: 0,
    pollingCancelled: false,
    progressMessages: [],
    progressMessageKeys: {},
    progressMessageIndex: -1,
    progressMessageTimer: null,
    progressMessageDrainResolvers: [],
    creationResult: null,
    nameValid: false,
    rules: {
      validName: (value) => {
        let pattern = /^(\w|\.|-)+$/;
        if (!pattern.test(value)) {
          return "given name contains invalid characters";
        }
        return true;
      },
    },
  }),
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  watch: {
    value(value) {
      if (value) {
        // Here you would put something to happen when dialog opens up
        console.log("Application Create Dialog was opened!");
        this.environments = this.$userData.cass.environments;
        this.environments.forEach(function(part, index, theArray) {
          theArray[index] = part.toUpperCase();
        });
      } else {
        console.log("Application Create Dialog was closed!");
        this.pollingCancelled = true;
        this.resetProgressMessages();
        this.creating = false;
        this.creationResult = null;
      }
    },
  },
  beforeDestroy() {
    this.pollingCancelled = true;
    this.resetProgressMessages();
  },
  methods: {
    notEmpty(value) {
      if (value == null || value == undefined) {
        return "Required";
      }
      if (value.length == "") {
        return "Required";
      }
      return true;
    },
    nop() {},
    min(v) {
      return (v || "").length >= 5 || `Name must be at least 5 characters long`;
    },
    close() {
      this.pollingCancelled = true;
      this.resetProgressMessages();
      this.creating = false;
      this.creationResult = null;
      this.show = false;
      if (this.$refs.formCreate) {
        this.$refs.formCreate.reset();
      }
      if (this.$refs.formImport) {
        this.$refs.formImport.reset();
      }
    },
    async createApplication() {
      if (this.creating) {
        return;
      }
      this.creating = true;
      this.pollingCancelled = false;
      this.demoStep = 0;
      this.creationProgress = "Submitting application creation";
      this.resetProgressMessages();
      try {
        const response = await cass.createApplication({
          environment: this.selectedEnvironment,
          permissionModel: this.selectedPermissionModel,
          displayName: this.appNameCreate,
        });
        if (!response.data || !response.data.operationId) {
          throw new Error("Application creation did not return an operation ID.");
        }
        const operation = await waitForApplicationCreationOperation({
          operationId: response.data.operationId,
          getOperation: cass.getApplicationCreationOperation.bind(cass),
          onUpdate: (currentOperation) => {
            this.demoStep = progressStepByState[currentOperation.progress] ?? 0;
            this.appendProgressMessages(currentOperation);
          },
          isCancelled: () => this.pollingCancelled,
          sleep: this.Sleep,
        });

        if (!operation || this.pollingCancelled) {
          return;
        }
        await this.waitForProgressMessages();
        if (this.pollingCancelled) {
          return;
        }
        if (operation.status === "failed") {
          this.creationResult = applicationCreationResultFromOperation(operation);
          return;
        }
        if (operation.status === "succeeded" && operation.applicationId) {
          this.$router
            .push({
              name: "Application",
              params: { applicationid: operation.applicationId },
            })
            .catch(() => {
              window.location.reload();
            });
          this.close();
          return;
        }
        this.creationResult = applicationCreationResultFromError(new Error("Application creation completed without an application ID."));
      } catch (err) {
        if (this.pollingCancelled) {
          return;
        }
        this.creationResult = applicationCreationResultFromError(err);
      } finally {
        if (!this.pollingCancelled) {
          this.creating = false;
        }
      }
    },
    openApplication(applicationId) {
      this.$router.push({ name: "Application", params: { applicationid: applicationId } }).catch(() => window.location.reload());
      this.close();
    },
    Sleep(milliseconds) {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    },
    appendProgressMessages(operation) {
      const messages = Array.isArray(operation.messages) ? operation.messages : [];
      let added = false;
      messages.forEach((message) => {
        if (!message || typeof message.message !== "string" || message.message === "") {
          return;
        }
        const key = [message.at, message.stage, message.status, message.message].join("|");
        if (this.progressMessageKeys[key]) {
          return;
        }
        this.$set(this.progressMessageKeys, key, true);
        this.progressMessages.push(message.message);
        added = true;
      });
      if (this.progressMessageIndex === -1 && this.progressMessages.length > 0) {
        this.showNextProgressMessage();
      }
      if (!added && this.progressMessages.length === 0) {
        this.creationProgress = applicationCreationProgressLabel(operation);
      }
      this.startProgressMessageRotation();
    },
    showNextProgressMessage() {
      if (this.progressMessageIndex >= this.progressMessages.length - 1) {
        return false;
      }
      this.progressMessageIndex += 1;
      this.creationProgress = this.progressMessages[this.progressMessageIndex];
      return true;
    },
    startProgressMessageRotation() {
      if (this.progressMessageTimer) {
        return;
      }
      if (this.progressMessageIndex >= this.progressMessages.length - 1) {
        this.resolveProgressMessageDrain();
        return;
      }
      this.progressMessageTimer = setInterval(() => {
        if (this.progressMessageIndex >= this.progressMessages.length - 1) {
          clearInterval(this.progressMessageTimer);
          this.progressMessageTimer = null;
          this.resolveProgressMessageDrain();
          return;
        }
        this.showNextProgressMessage();
      }, 1500);
    },
    waitForProgressMessages() {
      if (this.pollingCancelled || this.progressMessageIndex >= this.progressMessages.length - 1) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        this.progressMessageDrainResolvers.push(resolve);
        this.startProgressMessageRotation();
      });
    },
    resolveProgressMessageDrain() {
      const resolvers = this.progressMessageDrainResolvers;
      this.progressMessageDrainResolvers = [];
      resolvers.forEach((resolve) => resolve());
    },
    resetProgressMessages() {
      if (this.progressMessageTimer) {
        clearInterval(this.progressMessageTimer);
      }
      this.progressMessageTimer = null;
      this.progressMessages = [];
      this.progressMessageKeys = {};
      this.progressMessageIndex = -1;
      this.resolveProgressMessageDrain();
    },
  },
};
</script>

<style>
.build-progress {
  min-height: 390px;
  padding: 34px 48px 40px;
  text-align: center;
}

.build-kicker {
  color: #006c27;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.build-progress h2 {
  margin: 8px 0;
  color: #173b22;
  font-size: 24px;
  font-weight: 600;
}

.build-progress p {
  margin: 0 auto 24px;
  color: #5b655d;
}

.front,
.back {
  transform-style: unset !important;
  -webkit-transform-style: unset !important;
}
</style>
